import { AfterViewInit, Component, EventEmitter, HostListener, Input, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { SortEvent } from 'primeng/api';
import { Popover } from 'primeng/popover';
import { SelectChangeEvent } from 'primeng/select';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { ActionTable } from '../../../core/models/common/action-table.model';
import { CustomDataComponent } from '../../../core/models/common/custom-data-component.model';
import { FieldColumn } from '../../../core/models/common/filed-column.model';
import { PaginationOptions } from '../../../core/models/common/pagination-options.model';
import { SortOrder } from '../../constants/sort-order';
import { UtcDatePipe } from '../../pipes/utc-date.pipe';

export type Severity = "success" | "secondary" | "info" | "warn" | "danger" | "contrast";

@Component({
  selector: 'app-admin-table',
  standalone: false,
  templateUrl: './admin-table.component.html',
  styleUrl: './admin-table.component.scss'
})
export class AdminTableComponent<T> implements AfterViewInit {
  // Add pipe instance
  private utcDatePipe = new UtcDatePipe();

  @ViewChildren('filterPopover') filterPopovers!: QueryList<Popover>;

  @ViewChild('dt') dt: Table | undefined;
  showLeftIndicator = false;
  showRightIndicator = false;

  @Input() lazy = false; // True = remoto, false = local
  @Input() totalRecords: number | null = null; // Total de registros en el servidor
  @Output() onLazyLoad: EventEmitter<PaginationOptions> = new EventEmitter();

  @Input() columns: FieldColumn[] = [];
  @Input() actions: CustomDataComponent[] | ((row: T) => CustomDataComponent[]) = [];
  @Input() headerButtons: CustomDataComponent[] = [];
  @Input() data: T[] = [];
  @Input() showSearch = false;
  @Input() paginator = true;
  @Input() rows: number | null = null;

  @Output() actionClick: EventEmitter<ActionTable<T>> = new EventEmitter();
  @Output() headerButtonClick: EventEmitter<string> = new EventEmitter();

  sortField: string | undefined;
  sortOrder: SortOrder = SortOrder.NO_SORT;

  filteredField: Record<string, string> | null = null;

  private activePopoverField: string | null = null;

  constructor() {
  }

  ngOnInit() {

  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.attachScrollListener();
      this.updateScrollIndicators();
    }, 0);
  }

  @HostListener('window:resize')
  onResize() {
    console.log('Window resized');
    this.updateScrollIndicators();
  }

  private attachScrollListener() {
    const scrollBody = this.dt?.el.nativeElement.querySelector('.p-datatable-table-container');
    if (scrollBody) {
      scrollBody.addEventListener('scroll', () => this.updateScrollIndicators());
    }
  }

  getRowActions(row: T): CustomDataComponent[] {
    if (typeof this.actions === 'function') {
      return this.actions(row);
    }
    return this.actions as CustomDataComponent[];
  }

  updateScrollIndicators() {
    const scrollBody = this.dt?.el.nativeElement.querySelector('.p-datatable-table-container');
    if (scrollBody) {
      const scrollLeft = scrollBody.scrollLeft;
      const maxScrollLeft = scrollBody.scrollWidth - scrollBody.clientWidth;
      this.showLeftIndicator = scrollLeft > 5;
      this.showRightIndicator = scrollLeft < maxScrollLeft - 5;
    } else {
      this.showLeftIndicator = false;
      this.showRightIndicator = false;
    }
  }

  customSort(event: SortEvent) {
    if (event.data && event.multiSortMeta && event.multiSortMeta.length > 0 && event.multiSortMeta[0].order) {
      let currentSortOrder = (event.multiSortMeta[0].order as unknown as SortOrder);
      let currentSortField = event.multiSortMeta[0].field as string | undefined;

      if (this.sortField === currentSortField && currentSortOrder === SortOrder.ASC &&
        this.sortOrder === SortOrder.DESC
      ) {
        this.sortField = undefined;
        this.sortOrder = SortOrder.NO_SORT;
        this.dt?.reset();
      }
      else {
        this.sortField = currentSortField ?? undefined;
        this.sortOrder = (currentSortOrder !== undefined ? currentSortOrder : SortOrder.ASC);
      }
    }
  }

  sortTableData(event: SortEvent) {
    if (!event.data) {
      return;
    }
    event.data.sort((data1, data2) => {
      const field = event.field as string | undefined;
      let value1 = field ? data1[field] : undefined;
      let value2 = field ? data2[field] : undefined;
      let result = null;
      if (value1 == null && value2 != null) result = -1;
      else if (value1 != null && value2 == null) result = 1;
      else if (value1 == null && value2 == null) result = 0;
      else if (typeof value1 === 'string' && typeof value2 === 'string') result = value1.localeCompare(value2);
      else result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;

      return (event.order ?? 1) * result;
    });
  }

  rowsPerPageOptions = [10, 20, 50, 100].map(v => ({ label: v.toString(), value: v }));

  private ignoreNextLazyLoad = false;

  handleLazyLoad(event: TableLazyLoadEvent) {
    if (this.ignoreNextLazyLoad) {
      this.ignoreNextLazyLoad = false;
      return;
    }
    if (this.lazy && event.first !== undefined && event.rows !== undefined && event.rows) {
      const currentPage = Math.floor(event.first / event.rows);

      let currentSortOrder = event.sortOrder as unknown as SortOrder;
      let currentSortField = event.sortField as string | undefined;

      if (this.sortField === currentSortField && currentSortOrder === SortOrder.ASC &&
        this.sortOrder === SortOrder.DESC
      ) {
        this.sortField = undefined;
        this.sortOrder = SortOrder.NO_SORT;
        this.resetTable();
      } else {
        this.sortField = currentSortField ?? undefined;
        this.sortOrder = (currentSortOrder !== undefined ? currentSortOrder : SortOrder.ASC);
      }
      this.sortOrder = this.sortField ? (this.sortOrder !== SortOrder.NO_SORT ? this.sortOrder : SortOrder.ASC) : SortOrder.NO_SORT;


      // console.log('Pagination options:', { page: currentPage, size: event.rows, sortBy: this.sortField, sortDir: this.sortOrder, filtered: this.filteredField , search: this.searchTerm });
      let options: PaginationOptions = {
        page: currentPage,
        size: event.rows,
        sortBy: this.sortField,
        sortDir: this.sortOrder === SortOrder.NO_SORT ? undefined : (this.sortOrder === SortOrder.ASC ? 'asc' : 'desc'),
        filters: this.filteredField,
        search: this.searchTerm
      };
      this.callLazyLoad(options);
    }
  }

  callLazyLoad(options: PaginationOptions) {
    this.onLazyLoad.emit(options);
  }

  resetTable() {
    this.ignoreNextLazyLoad = true;
    this.dt?.reset();
  }

  get filteredData(): T[] {
    let result = this.data;
    if (!this.lazy && this.searchTerm && this.searchTerm.trim() !== '') {
      const term = this.searchTerm.toLowerCase();
      result = result.filter(row =>
        this.columns.some(col => {
          const key = col.field as keyof T;
          const value = row[key];
          return value && value.toString().toLowerCase().includes(term);
        })
      );
    }

    const filteredColumns = this.columns.filter(col => col.filter && col.filterValue != null && col.filterValue !== '');
    if (filteredColumns.length > 0) {
      result = result.filter(row =>
        filteredColumns.every(col => {
          const key = col.field as keyof T;
          const value = row[key];
          return value != null && value.toString() === col.filterValue?.toString();
        })
      );
    }

    if (!this.lazy && this.sortField) {
      result = [...result].sort((a, b) => {
        const key = this.sortField as keyof T;
        const value1 = a[key];
        const value2 = b[key];
        let res = 0;
        if (value1 == null && value2 != null) res = -1;
        else if (value1 != null && value2 == null) res = 1;
        else if (value1 == null && value2 == null) res = 0;
        else if (typeof value1 === 'string' && typeof value2 === 'string') res = value1.localeCompare(value2);
        else res = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;
        return (typeof this.sortOrder === 'number' ? this.sortOrder : Number(this.sortOrder)) * res;
      });
    }
    return result;
  }

  set searchTerm(val: string) {
    this._searchTerm = val;
    this.dt?.reset();
  }
  get searchTerm(): string {
    return this._searchTerm;
  }
  private _searchTerm = '';


  countRecords(): number {
    return this.data ? this.data.length : 0;
  }

  getFilteredOptions(col: FieldColumn): { label: string, value: string }[] {
    const uniqueValues = Array.from(
      new Set(
        this.data.map(row => {
          const key = col.field as keyof T;
          return row[key];
        })
      )
    );
    return col.filterOptions
      ? col.filterOptions.filter(opt =>
        uniqueValues.map(v => v != null ? v.toString() : '').includes(opt.value != null ? opt.value.toString() : '')
      )
      : [];
  }

  openFilterPopover(col: FieldColumn, event: Event) {
    const popover = this.filterPopovers?.find(p => p.el.nativeElement.id === col.field);
    if (popover) {
      popover.toggle(event);
      this.activePopoverField = col.field;
    }
  }

  getSeverity(col: FieldColumn, row: T): Severity | undefined {
    if (!col?.filterOptions || !row) return undefined;
    const key = col.field as keyof T;
    const value = row[key];
    const found = col.filterOptions.find(opt => opt.value === value);
    return found?.severity as Severity | undefined;
  }

  filterTable(value: string | null, col: FieldColumn) {
    if (value === null || value === '' || col.filterValue === value) {
      col.filterValue = undefined;
      this.filteredField = null;
    } else {
      col.filterValue = value;
      this.filteredField = { [col.field]: value };
    }
    this.dt?.reset();

    if (this.activePopoverField) {
      const popover = this.filterPopovers?.find(p => p.el.nativeElement.id === this.activePopoverField);
      if (popover) {
        popover.hide();
        this.activePopoverField = null;
      }
    }
  }

  getSeguroClass(fecha: Date | string | null | undefined): string {
    if (!fecha) return '';
    const hoy = new Date();
    const fechaVenc = new Date(fecha);
    if (
      fechaVenc.getFullYear() < hoy.getFullYear() ||
      (fechaVenc.getFullYear() === hoy.getFullYear() && fechaVenc.getMonth() <= hoy.getMonth())
    ) {
      return 'expired';
    }
    return '';
  }

  getCellValue(row: T, col: FieldColumn): string {
    let nestedValue = this.getNestedValue<T>(row, col.field);
    if (nestedValue && col.objectData && Array.isArray(col.objectData)) {
      const values = col.objectData.map(path => this.getNestedValue(row, path));
      return values.filter(v => v !== undefined && v !== null && v !== '').join(' ');
    } else {
      const value = nestedValue;
      if ((value === null || value === undefined || value === '') && col.emptyText) {
        return col.emptyText;
      }
      // Format dates using UTC pipe
      if (col.type === 'date' && value) {
        return this.utcDatePipe.transform(value as string | Date, 'long');
      }
      return value != null ? value.toString() : '';
    }
  }

  getNestedValue<TObj, TResult = unknown>(obj: TObj, path: string): TResult | undefined {
    return path.split('.').reduce((acc: unknown, part) => {
      if (acc && typeof acc === 'object' && part in acc) {
        return (acc as Record<string, unknown>)[part];
      }
      return undefined;
    }, obj) as TResult | undefined;
  }

  parseCss(cssString?: string): { [key: string]: string } {
    if (!cssString) return {};
    return cssString
      .split(';')
      .filter(rule => rule.trim())
      .reduce((acc, rule) => {
        const [prop, value] = rule.split(':');
        if (prop && value) acc[prop.trim()] = value.trim();
        return acc;
      }, {} as { [key: string]: string });
  }

  onRowsChange(event: SelectChangeEvent) {
    const value = typeof event.value === 'string'
      ? event.value.replace(/\D/g, '')
      : event.value;
    const numValue = Number(value.value);
    if (numValue > 0) {
      this.rows = numValue;
      let options: PaginationOptions = {
        page: 0,
        size: this.rows,
      };
      this.resetTable();
      if (this.lazy) {
        this.callLazyLoad(options);
      }
    }
  }
}
